import { ENDPOINTS } from 'config/endpoints';
import CronoError from 'crono-fe-common/types/error';
import { LogGeneric } from 'crono-fe-common/types/logGeneric';
import { invalidateActivitiesQueries } from 'hooks/services/event/useActivitiesSearch';
import { invalidateTimelineQueries } from 'hooks/services/timeline/useSearchTimeline';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import client from 'utils/clients/client';

export const useLogGeneric = () => {
  const queryClient = useQueryClient();

  const insertGeneric = (logGeneric: LogGeneric) => {
    const request: Request = {
      url: `${ENDPOINTS.events.generic}`,
      config: {
        method: 'post',
        data: { data: logGeneric },
      },
    };
    return client(request);
  };

  const { data, mutate, ...rest } = useMutation<
    Response<null>,
    CronoError,
    LogGeneric
  >(insertGeneric, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([ENDPOINTS.events.task.main]);
      queryClient.invalidateQueries([ENDPOINTS.events.event]);
      queryClient.invalidateQueries([ENDPOINTS.task.search]);
      queryClient.invalidateQueries([ENDPOINTS.task.main]);
      queryClient.invalidateQueries([ENDPOINTS.task.aggregate]);
      queryClient.invalidateQueries([ENDPOINTS.task.total]);
      queryClient.invalidateQueries([ENDPOINTS.task.dailyQuantity]);
      // WARNING: this is commented since the next task is called from the
      // context of multiple execution manually, to improve synchronization
      // If re-enabled: completing a task inside the multi-task flow refetches
      // /task/search/next with the just-completed id before the flow advances its own
      // query key — the BE 400s on the already-handled id (and can
      // replay/reopen the handled task).
      // queryClient.invalidateQueries([ENDPOINTS.task.next]),
      queryClient.invalidateQueries([ENDPOINTS.task.overdue]);
      queryClient.invalidateQueries([ENDPOINTS.task.user.analytics]);
      queryClient.invalidateQueries([ENDPOINTS.user.onboarding.features]);

      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.toString().startsWith(ENDPOINTS.strategy.main),
      });
      queryClient.invalidateQueries([ENDPOINTS.strategy.search]);
      queryClient.invalidateQueries([ENDPOINTS.strategy.details]);

      queryClient.invalidateQueries([
        ENDPOINTS.account.main,
        variables.accountId,
      ]);
      queryClient.invalidateQueries([ENDPOINTS.account.search.main]);
      queryClient.invalidateQueries([ENDPOINTS.prospect.search]);
      queryClient.invalidateQueries([ENDPOINTS.task.automationErrors]);

      queryClient.invalidateQueries([ENDPOINTS.analytics.performance.main]);
      invalidateActivitiesQueries(queryClient);
      invalidateTimelineQueries(queryClient);
    },
  });

  return { data, mutate, ...rest };
};
